import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import Button from '../../../components/Button';
import Loading from '../../../components/Loading';
import Paginacao from '../../../components/Paginacao';
import UnidadeSaudeCadastro, { IUnidadeSaude } from '../UnidadeSaudeCadastro';

import Modal from '../../../components/Modal';

import { IFormBusca } from '..';
import getResponseErrors from '../../../utils/getResponseErrors';

export interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

// export interface RefObject {
//   addMedico(dadosMedico: IUnidadeSaude, MedCodigo: number | undefined): Promise<void>;
// }
type Props = {
  dados?: IFormBusca;
};

const ListaUnidadesSaude: React.FC<Props> = forwardRef((prop: Props) => {
  const [showModalCadastro, setShowModalCadastro] = useState(false);
  const { addToast } = useToast();
  const [paginacao, setPaginacao] = useState<IPaginacao | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const [unidades, setUnidades] = useState<IUnidadeSaude[]>([]);

  const [unidadeSelecionada, setUnidadeSelecionada] = useState<
    IUnidadeSaude | undefined
  >(undefined);

  useEffect(() => {
    setPaginacao({
      termoBusca: prop.dados || { nome: '', bairro: '', cidade: '' },
      pagina: 1,
      limite: 20,
    });
  }, [prop.dados]);

  useEffect(() => {
    async function buscaUnidades(): Promise<void> {
      if (paginacao) {
        try {
          setLoading(true);
          const { nome, bairro, cidade } = paginacao.termoBusca;
          let query = '';
          if (nome !== '') query = `&UnsNome=${nome}`;
          if (bairro !== '') query = `${query}&UnsBairro=${bairro}`;
          if (cidade !== '') query = `${query}&UnsCidade=${cidade}`;

          const response = await api.get(
            `/unidadeSaude?pagina=${paginacao.pagina}&limite=${paginacao.limite}${query}`,
          );

          const unidadesApi: IUnidadeSaude[] = response.data.dados;

          const unidadesFormatadas = unidadesApi.map((uni: IUnidadeSaude) => {
            return {
              ...uni,
            };
          });

          setTotalRegistros(
            response.data.total === 0 ? undefined : response.data.total,
          );

          setUnidades(unidadesFormatadas);
        } catch (error) {
          console.log(error);
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar Unidades de Saúde',
            description: mensagem,
          });
          setTotalRegistros(0);
          setUnidades([]);
        } finally {
          setLoading(false);
        }
      }
    }
    if (paginacao) buscaUnidades();
  }, [paginacao, addToast]);

  const handleDeleteUnidade = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        await api.delete(`/unidadeSaude/${codigo}`);
        setUnidades(unidadesState =>
          unidadesState?.filter(uni => uni.UnsCodigo !== codigo),
        );
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível deletar Unidade de Saúde',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  const handleEditar = useCallback(medico => {
    setUnidadeSelecionada(medico);
    setShowModalCadastro(true);
  }, []);

  return (
    <>
      {showModalCadastro && (
        <Modal
          show={showModalCadastro}
          titulo="Cadastro de Unidades de Saúde p/Encaminhamento"
          handleFecharModal={() => setShowModalCadastro(false)}
          large
        >
          <UnidadeSaudeCadastro
            unidadeSaude={unidadeSelecionada}
            setShowModal={setShowModalCadastro}
          />
        </Modal>
      )}
      {loading && <Loading />}

      {unidades && unidades.length > 0 ? (
        <>
          <table
            className="table table-hover table-sm table-font-small"
            style={{ borderWidth: '0px' }}
          >
            <tbody>
              <tr className="bg-light" style={{ fontSize: 'Small' }}>
                <th scope="col">Nome</th>
                <th scope="col">Bairro</th>
                <th scope="col">Cidade</th>
                <th scope="col">&nbsp;</th>
                <th scope="col">&nbsp;</th>
              </tr>
              {unidades.map((uni: IUnidadeSaude) => (
                <tr
                  key={`unidade-${uni.UnsCodigo}`}
                  style={{ fontSize: 'Small', height: '30px' }}
                >
                  <td>{uni.UnsNome}</td>
                  <td style={{ width: '100px' }}>{uni.UnsBairro}</td>
                  <td style={{ width: '60px' }}>{uni.UnsCidade}</td>
                  <td className="p-0" style={{ width: '40px' }}>
                    <Button
                      type="button"
                      variant="primary-outline"
                      className="py-0"
                      title={uni.UnsCodigo.toString()}
                      onClick={() => handleEditar(uni)}
                    >
                      <FaPencilAlt />
                    </Button>
                  </td>

                  <td className="p-0" style={{ width: '40px' }}>
                    <Button
                      type="button"
                      variant="secondary-outline"
                      className="py-0"
                      title={uni.UnsCodigo.toString()}
                      onClick={() => {
                        handleDeleteUnidade(uni.UnsCodigo);
                      }}
                    >
                      <FaTrashAlt />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginacao
            limitePagina={paginacao?.limite || 15}
            paginasVizinhas={2}
            totalRegistros={totalRegistros}
            paginaInicio={paginacao?.pagina}
            handleProximaPagina={handleProximaPagina}
          />
        </>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '250px' }}
        >
          <h5>
            Nenhum registro encontrado. Pesquise Unidades de Saúde pelo Nome,
            bairro e/ou cidade
          </h5>
        </div>
      )}
    </>
  );
});

export default ListaUnidadesSaude;
